/**
 * Organization session management utilities
 * Handles localStorage persistence for organization data across Auth0 login redirects
 */

import { Organization } from '../types';

const ORGANIZATION_STORAGE_KEY = 'prodagen_organization';

/**
 * Store organization data in localStorage
 * @param organization The organization object to persist
 */
export const storeOrganization = (organization: Organization): void => {
  try {
    localStorage.setItem(ORGANIZATION_STORAGE_KEY, JSON.stringify(organization));
  } catch (error) {
    console.warn('Failed to store organization in localStorage:', error);
  }
};

/**
 * Retrieve organization data from localStorage
 * @returns The stored organization object or null if not found
 */
export const getStoredOrganization = (): Organization | null => {
  try {
    const stored = localStorage.getItem(ORGANIZATION_STORAGE_KEY);
    if (stored) {
      return JSON.parse(stored) as Organization;
    }
    return null;
  } catch (error) {
    console.warn('Failed to retrieve organization from localStorage:', error);
    return null;
  }
};

/**
 * Clear stored organization data from localStorage
 */
export const clearStoredOrganization = (): void => {
  try {
    localStorage.removeItem(ORGANIZATION_STORAGE_KEY);
  } catch (error) {
    console.warn('Failed to clear organization from localStorage:', error);
  }
};

/**
 * Get organization_id from either URL or localStorage
 * Priority: URL organization > localStorage organization
 * @param urlOrgToken Organization token from URL path
 * @param urlOrgQuery Organization from URL query parameter  
 * @param storedOrganization Organization from localStorage
 * @returns organization_id to use for Auth0, or null if none available
 */
export const resolveOrganizationId = (
  urlOrgToken: string | null,
  urlOrgQuery: string | null, 
  storedOrganization: Organization | null
): string | null => {
  // URL query parameter takes highest priority (direct org_id)
  if (urlOrgQuery) {
    return urlOrgQuery;
  }
  
  // URL path token takes second priority (needs to be resolved via API)
  // This will be handled by the fetchOrganizationByToken logic
  if (urlOrgToken) {
    return null; // Signal that we need to fetch by token
  }
  
  // Fall back to stored organization
  if (storedOrganization?.organization_id) {
    return storedOrganization.organization_id;
  }
  
  return null;
};
